import {
	ObjectRendererBase,
	ObjectRendererInterface,
} from "@animech/axiom-scene-builder";
import { Entity, Vec3 } from "playcanvas";
import { assert } from "@animech/core-utilities";
import { createElement, ReactElement } from "react";
import { UiManager } from "./ui-manager";
import { Root } from "@definitions";
import { SceneObject } from "src/common";
import { calculateBoundingBoxForEntity, isMobile } from "src/utility";

type ElementInfo = {
	tree: ReactElement;
	element: HTMLDivElement;
};

export class UiRenderer extends ObjectRendererBase<Root> {
	private _uiManager: UiManager;
	private _elements = new Map<SceneObject, ElementInfo>();
	private _warningElements = new Map<SceneObject, ElementInfo | undefined>();
	private _canvas: HTMLCanvasElement;

	constructor(manager: UiManager, canvas: HTMLCanvasElement) {
		super();
		this._uiManager = manager;
		this._canvas = canvas;
	}

	onObjectBuilt(
		object: SceneObject,
		_entity: Entity,
		rendererInterface: ObjectRendererInterface<Root>,
	): void {
		const { addUpdateDependency } = rendererInterface;

		this._uiManager.addSceneObject(object);

		if (object.ui || object.template) {
			addUpdateDependency(object);
		}
	}

	onObjectDeleted(object: SceneObject, _entity: Entity): void {
		const elementInfo = this._elements.get(object);
		const warningElementInfo = this._warningElements.get(object);
		if (elementInfo) {
			this._uiManager.removeSceneElement(elementInfo.tree);
			this._elements.delete(object);
		}
		if (warningElementInfo) {
			this._uiManager.removeOutOfStockWarningElement(warningElementInfo.tree);
			this._warningElements.delete(object);
		}

		this._uiManager.removeSceneObject(object);
	}

	updateEntity(
		object: SceneObject,
		entity: Entity,
		rendererInterface: ObjectRendererInterface<Root>,
	): void {
		const { isSelected } = rendererInterface;
		const elementInfo = this._elements.get(object);
		const warningElementInfo = this._warningElements.get(object);
		const isObjectInStock = this._uiManager.isSceneObjectInStock(object);
		if (warningElementInfo) {
			if (isObjectInStock) {
				this._uiManager.removeOutOfStockWarningElement(warningElementInfo.tree);
				this._warningElements.delete(object);
			} else {
				this._updateWarningElementStyle(
					warningElementInfo.element,
					entity,
					rendererInterface,
				);
			}
		} else if (!isObjectInStock) {
			const tree = object.ui
				? this._uiManager.builder.buildTree(object.ui)
				: createElement("span");
			if (tree) {
				this._uiManager.addOutOfStockWarningElement(tree, (ref) => {
					const element = ref as HTMLDivElement;
					if (element) {
						this._warningElements.set(object, { tree, element });
						this._updateWarningElementStyle(element, entity, rendererInterface);
					}
				});
			}
		}
		if (elementInfo) {
			if (isSelected(object)) {
				this._updateElementStyle(
					elementInfo.element,
					entity,
					rendererInterface,
				);
			} else {
				this._uiManager.removeSceneElement(elementInfo.tree);
				this._elements.delete(object);
			}
		} else if (isSelected(object)) {
			assert(object.ui);
			const tree = this._uiManager.builder.buildTree(object.ui);
			if (tree) {
				this._uiManager.addSceneElement(tree, (ref) => {
					const element = ref as HTMLDivElement;
					if (element) {
						this._elements.set(object, { tree, element });
						this._updateElementStyle(element, entity, rendererInterface);
					}
				});
			}
		}
	}

	private _updateElementStyle(
		element: HTMLDivElement,
		entity: Entity,
		rendererInterface: ObjectRendererInterface<Root>,
	) {
		const { cameraComponent } = rendererInterface;
		const bbox = calculateBoundingBoxForEntity(entity, true);
		const screenPositionLeftCenter = cameraComponent.worldToScreen(
			new Vec3(
				bbox?.getMin().x || entity.getPosition().x,
				bbox?.center.y || entity.getPosition().y,
				entity.getPosition().z,
			),
		);

		// center the elements on mobile screens
		if (isMobile()) {
			element.style.left = `50%`;
			element.style.top = `50%`;
		} else {
			const canvasRect = this._canvas.getBoundingClientRect();
			const xOffset = -125; // show the component to the left of the component

			element.style.left = `${screenPositionLeftCenter.x + xOffset + canvasRect.left}px`;
			element.style.top = `${screenPositionLeftCenter.y}px`;
		}
	}

	private _updateWarningElementStyle(
		element: HTMLDivElement,
		entity: Entity,
		rendererInterface: ObjectRendererInterface<Root>,
	) {
		const { cameraComponent } = rendererInterface;
		const bbox = calculateBoundingBoxForEntity(entity, true);
		const screenPositionCenter = cameraComponent.worldToScreen(
			new Vec3(
				bbox?.center.x || entity.getPosition().x,
				bbox?.getMax().y || entity.getPosition().y,
				entity.getPosition().z,
			),
		);

		const canvasRect = this._canvas.getBoundingClientRect();

		element.style.left = `${screenPositionCenter.x + canvasRect.left}px`;
		element.style.top = `${screenPositionCenter.y}px`;
	}
}
